import React, { useCallback, useEffect, useState, ReactElement, useMemo } from 'react';
import { Customer, QuickTimes, QuickTimeRec, UpdateApptDateInp, VehicleRecs, DepartmentSpec, Appointment, QuickTimeInp, invalidDates, ApptCust, ApptOps, CheckedRecall, ascPageviewInput, ascCtaInteractionInput, ascRetailProcessInput, ascElementConfigurationInput, SpecData } from 'src/models';
import { strings } from 'src/localization';
import { IView } from 'Framework';
import * as css from './QuickTime.module.scss';
import { Avatar, LoadingProgress, ScrollBar, Dialog } from '@framework-controls/core';
import QuickTimeCard from './QuickTimeCard/QuickTimeCard';
import Footer from '../Footer/Footer';
import { ComponentMapIds } from '../map';
import NoProfile from 'src/content/icons/Profile2.svg';
import NoProfileAccess from 'src/content/icons/ProfileWhite.svg';
import { IconRemove, IconHelpCircleSm, IconProfile2 } from 'src/content/icons';
import SVPButton from '../SVPButtons/SVPButton';
import { ascPageview, ascCtaInteraction, ascRetailProcess, ascElementConfiguration, getElementColor } from 'src/helpers/EventHelper/EventHelper';
import classNames from 'classnames';
import { sendSdEvent, getProviderId, buildApptData } from 'src/helpers/ShiftDigitalHelper/ShiftDigitalHelper';
import { IAppointmentState, IQuickTimesState } from 'src/store/states';

export interface IProps {
    customerInfo?: Customer;
    quickTimes?: QuickTimes;
    qtimeSlots?: QuickTimeRec[];
    quickTimesSlice: IQuickTimesState;
    specProfile: string;
    vehicles: VehicleRecs;
    selectedVehicle?: string;
    specDeptData: DepartmentSpec;
    appointment: Appointment;
    invalidDates: invalidDates[];
    invalidDatesDept: string;
    theme: string;
    apptDept: string;
    apptCust: ApptCust;
    lastDeptRan: string;
    recallFlagUsed: boolean;
    apptOps: ApptOps[];
    checkedRecalls: CheckedRecall[];
    locale: string;
    gmossVersion: string;
    mainPaneViewOrder: IView[];
    specData?: SpecData;
    sessionId: string;
    lexusMCLaunched: string;
    toyotaMCLaunched: string;
    lexusTDMCLaunched: string;
    toyotaTDMCLaunched: string;
    appointmentData: IAppointmentState;
    getQuickTimes: (inp: QuickTimeInp, requestId?: number) => void;
    getRMTimes: (specProfile) => void;
    getInvalidDates: (specProfile: string, selectedDate?: undefined, selectedAdv?: undefined, recallSelected?: boolean) => void;
    pushToMainPanes: (inputView: IView) => void;
    updateApptDate: (inp: UpdateApptDateInp) => void;
    clearInvalidDates: () => void;
    addLogEntry: (action: string, screen: string, details: string) => void;
    popFromMainPane: (id: string) => void;
    clearQuickTimes: () => void;
}

const QuickTime = (props: IProps) => {
    const [footerDisabled, setFooterDisabled] = useState<boolean>(true);
    const [fetchingQuickTimes, setFetchingQuickTimes] = useState(false);
    const [header, setHeader] = useState('');
    const [subHeaderA, setSubHeaderA] = useState('');
    const [subHeaderB, setSubHeaderB] = useState('');
    const [checkId, setCheckId] = useState('');
    const [moveFlag, setMoveFlag] = useState('');
    const [noProfileImage, setNoProfileImage] = useState('');
    const [displayAdvName, setDisplayAdvName] = useState('');
    const [displayAdvImage, setDisplayAdvImage] = useState('');
    const [displayAdvImgAltTag, setDisplayAdvImgAltTag] = useState('');
    const [noFilteredTimes, setNoFilteredTimes] = useState(false);
    const [infoOpen, setInfoOpen] = useState(false);
    const [transInfo, setTransInfo] = useState('');
    const [pageViewEvent, setPageViewEvent] = useState(true);
    const [diffTimeSelected, setDiffTimeSelected] = useState(true);
    const [currentRequestId, setCurrentRequestId] = useState<number | null>(null);
    const isSDAnalyticsEnabled =
        props.specData?.toyotaTrackingFlag === 'Y' ||
        props.specData?.lexusTrackingFlag === 'Y' ||
        props.specData?.mbzTrackingFlag === 'Y' ||
        props.specData?.mazdaCATrackingFlag === 'Y' ||
        props.specData?.vwTrackingFlag === 'Y' ||
        props.specData?.jaguarTrackingFlag === 'Y' ||
        props.specData?.landRoverTrackingFlag === 'Y';

    useEffect(() => {
        props.addLogEntry('Screen Entry', 'QuickTimes', '');
    }, [
        props.addLogEntry,
    ]);

    // Clear checkId if the department changes
    useEffect(() => {
        setCheckId('');
        setFooterDisabled(true);
    }, [props.apptDept]);

    const { customerInfo } = props;
    const { specDeptData } = props;
    const firstName = props.apptCust ? props.apptCust.custFirstName : '';
    const quickType = specDeptData.recTimesCtgry;
    // const advName = props.selectedVehicle ? props.vehicles[props.selectedVehicle].lastAdvName : '';
    let transportation = props.quickTimes?.lastTransType ?? '';
    if (props?.qtimeSlots) {
        transportation = props.qtimeSlots[0]?.transFlag ?? '';
    }
    const transLit = props.specDeptData.Transportation.find(trans => trans.type === transportation)?.title ?? '';
    const transType = props.specDeptData.Transportation.find(trans => trans.type === transportation)?.type ?? '';
    const transDesc = props.specDeptData.Transportation.find(trans => trans.type === transportation)?.description ?? '';
    const defaultTransLit = props.specDeptData.Transportation.find(trans => trans.defaultFlag === true)?.title ?? '';
    const defaultTransType = props.specDeptData.Transportation.find(trans => trans.defaultFlag === true)?.type ?? '';
    const defaultTransDesc = props.specDeptData.Transportation.find(trans => trans.defaultFlag === true)?.description ?? '';
    let moreOptionsLit = '';

    useEffect(() => {
        if (isSDAnalyticsEnabled) {
            if (specDeptData.recTimesCtgry === 'A' &&
                props.mainPaneViewOrder[props.mainPaneViewOrder.length - 1].viewId === ComponentMapIds.QuickTime) {
                sendSdEvent('scheduleServiceTransportShown', {
                    scheduleServiceSessionId: props.sessionId,
                    scheduleServiceProvider: getProviderId(props.specData, props.toyotaMCLaunched,
                        props.toyotaTDMCLaunched, props.lexusMCLaunched, props.lexusTDMCLaunched),
                    scheduleServiceTransportType: '',
                });
            }
            sendSdEvent('scheduleServiceApptShown', buildApptData(props.sessionId,
                getProviderId(props.specData, props.toyotaMCLaunched, props.toyotaTDMCLaunched,
                    props.lexusMCLaunched, props.lexusTDMCLaunched), props.appointmentData),
            );
        }
    }, [specDeptData, props.sessionId, props.specData, props.toyotaMCLaunched, props.mainPaneViewOrder,
        props.lexusMCLaunched, props.lexusTDMCLaunched, props.toyotaTDMCLaunched, isSDAnalyticsEnabled,
        props.appointmentData]);

    useEffect(() => {
        setNoProfileImage(NoProfile);
        if (props.theme === 'colorSensitive' || props.theme === 'jeep' || props.theme === 'cadillac') {
            setNoProfileImage(NoProfileAccess);
        }
    }, [props.theme, setNoProfileImage]);
    // const advImage = props.selectedVehicle ? props.vehicles[props.selectedVehicle].lastAdvImage : noProfileImage;
    // const AdvImgAltTag = advName + ' ' + strings.Photo;

    useEffect(() => {
        if (moveFlag === 'T' && diffTimeSelected) {
            setMoveFlag('');
            if (noFilteredTimes) {
                props.popFromMainPane(ComponentMapIds.QuickTime);
            }
            props.pushToMainPanes({
                viewId: ComponentMapIds.CalendarSelect,
                component: ComponentMapIds.CalendarSelect,
            });
            setDiffTimeSelected(false);
        }
    }, [moveFlag, props.pushToMainPanes, props.popFromMainPane, noFilteredTimes, diffTimeSelected]);

    // Based on specs we either display an Advisor header or Transportation Header
    useEffect(() => {
        // if (props.specData?.QuickTimesType === 'A') {
        if (quickType === 'A') {
            if (props.qtimeSlots?.length === 0 || !props.qtimeSlots) {
                setHeader(strings.QuickTimesAdv);
                setSubHeaderA('');
                setSubHeaderB(strings.Transportation);
                const advNum = props.selectedVehicle ? props.vehicles[props.selectedVehicle].lastAdv : '';
                const advName = props.specDeptData.Advisors.find(adv => adv.advisorNum === advNum)?.advisorName ?? '';
                setDisplayAdvName(advName);
                const advImage = props.specDeptData.Advisors.find(adv => adv.advisorNum === advNum)?.advisorImage ?? '';
                const AdvImgAltTag = advName + ' ' + strings.Photo;
                setDisplayAdvImage(advImage);
                setDisplayAdvImgAltTag(AdvImgAltTag);
                if (props.specDeptData.defAdvOption === '1' || props.specDeptData.defAdvOption === '4') {
                    setHeader('');
                    const advNum = props.specDeptData.Advisors.find(adv => adv.defaultFlag === true)?.advisorNum ?? '';
                    const advName = props.specDeptData.Advisors.find(adv => adv.defaultFlag === true)?.advisorName ?? '';
                    setDisplayAdvName(advName);
                    const advImage = props.specDeptData.Advisors.find(adv => adv.advisorNum === advNum)?.advisorImage ?? '';
                    const AdvImgAltTag = advName + ' ' + strings.Photo;
                    setDisplayAdvImage(advImage);
                    setDisplayAdvImgAltTag(AdvImgAltTag);
                }
            } else {
                const modeFlag = props.qtimeSlots![0]?.modeFlag;
                const advNum = props.qtimeSlots![0]?.adv;
                const advName = props.specDeptData.Advisors.find(adv => adv.advisorNum === advNum)?.advisorName ?? '';
                setHeader(strings.QuickTimesAdv);
                setSubHeaderA('');
                setSubHeaderB(strings.Transportation);
                setDisplayAdvName(advName);
                const advImage = props.specDeptData.Advisors.find(adv => adv.advisorNum === advNum)?.advisorImage ?? '';
                const AdvImgAltTag = advName + ' ' + strings.Photo;
                setDisplayAdvImage(advImage);
                setDisplayAdvImgAltTag(AdvImgAltTag);
                if (modeFlag === '2' || modeFlag === '3') {
                    setHeader(strings.HereApptAdv);
                }
            }
            // setHeader(strings.QuickTimesAdv);
            // setSubHeaderA('');
            // setSubHeaderB(strings.Transportation);
            // If default transportation option is set to "Default"
        } else if (props.specDeptData.defTransOption === '2') {
            setHeader(strings.QuickTimesTransDefault);
            setSubHeaderA(defaultTransLit);
            setSubHeaderB(strings.Advisor);
        } else {
            setHeader(strings.QuickTimesTrans);
            setSubHeaderA(transLit);
            setSubHeaderB(strings.Advisor);
        }
    }, [quickType, transLit, props.specDeptData.defTransOption, defaultTransLit, props.specDeptData.defAdvOption,
        props.vehicles, props.selectedVehicle, props.qtimeSlots, props.specDeptData.Advisors, props.locale]);

    useEffect(() => {
        const isVisible =
            props.mainPaneViewOrder[props.mainPaneViewOrder.length - 1]?.viewId === ComponentMapIds.QuickTime;
        if (isVisible){
            setFetchingQuickTimes(true);
            setCheckId('');
            setFooterDisabled(true);
        }
    }, [props.mainPaneViewOrder]);

    // fetch data when screen becomes active
    useEffect(() => {
        const isActive =
            props.mainPaneViewOrder[props.mainPaneViewOrder.length - 1]?.viewId === ComponentMapIds.QuickTime;
        if (!isActive){
            return;
        }
        const vehicle = props.selectedVehicle ? props.vehicles[props.selectedVehicle] : null;
        if (!vehicle) {
            props.popFromMainPane(ComponentMapIds.QuickTime);
            props.pushToMainPanes({ viewId: ComponentMapIds.CalendarSelect, component: ComponentMapIds.CalendarSelect });

            return;
        }

        const requestId = Date.now();
        setCurrentRequestId(requestId);

        props.getQuickTimes({
            specProfile: props.specProfile,
            department: props.appointment.department,
            lastAdv: vehicle.lastAdv,
            lastTrans: vehicle.lastTrans,
            recallSelected: props.checkedRecalls.length > 0,
        }, requestId);
    }, [props.mainPaneViewOrder, props.selectedVehicle, props.vehicles, props.appointment.department, props.checkedRecalls.length,
        props.specProfile, props.getQuickTimes, props.pushToMainPanes, props.popFromMainPane]);

    useEffect(() => {
        if (
            props.quickTimesSlice?.builtAt !== null &&
            currentRequestId !== null &&
            props.quickTimesSlice.builtAt >= currentRequestId
        ) {
            setFetchingQuickTimes(false);
        }
    }, [props.quickTimesSlice.builtAt, currentRequestId]);
    useEffect(() => () => {
        props.clearQuickTimes();
        setCurrentRequestId(null);
    }, [props.clearQuickTimes]);


    const times = (props.qtimeSlots && props.qtimeSlots.length > 0) ? props.qtimeSlots : null;
    const onChecked = (id) => {
        if (checkId === id) {
            setCheckId('');
            setFooterDisabled(true);
        } else {
            setCheckId(id);
            setFooterDisabled(false);
        }
    };
    const diffTimeHandler = useCallback((event?: any) => {
        setDiffTimeSelected(true);
        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'service',
            elementText: strings.DifferentTime,
            elementColor: '',
            elementOrder: '',
            elementType: 'scheduling_tool',
            elementSubType: 'nav_button',
            eventAction: 'click',
            eventActionResult: 'check',
            linkUrl: '',
        };
        ascCtaInteraction(ascCtaInteractionInp, event, props.vehicles, props.selectedVehicle);
        props.clearInvalidDates();
        setMoveFlag('T');
    }, [
        props.clearInvalidDates,
        props.selectedVehicle,
        props.vehicles,
    ]);

    // Determine which transportation types are available.
    let waiterAvailable = true;
    let loanerAvailable = true;
    let rentalAvailable = true;
    let conciergeAvailable = true;
    let cadillacAvailable = true;
    let PPUDWLAvailable = true;
    props.apptOps.forEach((apptOp) => {
        if (apptOp.opWaiter === 'N') {
            waiterAvailable = false;
        }
        if (apptOp.opLoaner === 'N') {
            loanerAvailable = false;
        }
        if (apptOp.opRental === 'N') {
            rentalAvailable = false;
        }
        if (apptOp.opConcierge === 'N') {
            conciergeAvailable = false;
        }
        if (apptOp.opCadillac === 'N') {
            cadillacAvailable = false;
        }
        if (apptOp.opPPUDWL === 'N') {
            PPUDWLAvailable = false;
        }
    });
    let filteredTimeCards: QuickTimeRec[] = [];
    if (times && times[0].displayDate) {
        filteredTimeCards = times.filter(time => (
            (time.transFlag === 'W' && waiterAvailable) ||
            (time.transFlag === 'L' && loanerAvailable) ||
            (time.transFlag === 'R' && rentalAvailable) ||
            (time.transFlag === 'P' && conciergeAvailable) ||
            (time.transFlag === 'C' && cadillacAvailable) ||
            (time.transFlag === 'U' && PPUDWLAvailable) ||
            (time.transFlag !== 'W' && time.transFlag !== 'L' && time.transFlag !== 'R' &&
            time.transFlag !== 'C' && time.transFlag !== 'P' && time.transFlag !== 'U')
        ));
    }

    const infoHandler = useCallback((title: string, e: any) => {
        if (infoOpen && transInfo === title) {
            setInfoOpen(false);
        } else {
            setInfoOpen(true);
            setTransInfo(title);
        }
        const elementColor = getElementColor(e, 'color');
        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'service',
            elementText: '?',
            elementColor: elementColor,
            elementOrder: '',
            elementType: 'scheduling_tool',
            elementSubType: 'information_link',
            eventAction: 'click',
            eventActionResult: infoOpen ? 'close' : 'open',
            linkUrl: '',
        };
        ascCtaInteraction(ascCtaInteractionInp, e, props.vehicles, props.selectedVehicle);
    }, [transInfo, infoOpen, props.selectedVehicle, props.vehicles]);

    const onClick = useCallback((e) => {
        if (subHeaderA === defaultTransLit) {
            infoHandler(defaultTransType, e);
        } else if (subHeaderA === transLit) {
            infoHandler(transType, e);
        }
    }, [transType, infoHandler, defaultTransLit, subHeaderA, defaultTransType, transLit]);

    const closeInfoHandler = useCallback(() => {
        setInfoOpen(false);
    }, []);

    let TransinfoData = defaultTransDesc;
    if (subHeaderA === transLit) {
        TransinfoData = transDesc;
    }
    const infoIcon = TransinfoData !== '' && subHeaderA !== '' ?
        (
            <span id='transIcon' className={css.transIcon} onClick={onClick}>
                <IconHelpCircleSm
                    height={20}
                    width={18}
                />
            </span>
        ) :
        null;

    const transDescription = useMemo(() => {
        const transState = props.specDeptData.Transportation.find((trans) => trans.title === transInfo);
        const transDesc = transState?.description ?? '';
        const descList = transDesc.split('\n');
        const descDivs = descList.map((info, index) => <div key={index} className={css.infoDivs}>{info}</div>);
        const transTitle = transState?.title ?? '';
        if (infoOpen) {
            return (
                <Dialog
                    cssClasses={{
                        content: css.content,
                        paper: css.paper,
                        header: css.LightBoxHeader,
                    }}
                    isOpen={infoOpen}
                    anchorOrigin={{
                        vertical: 'top',
                    }}
                    header={transTitle}
                    hideSave={true}
                    hideCancel={true}
                    disableUpperCase={true}
                    onCancel={() => setInfoOpen(false)}
                    onBackDropClick={() => setInfoOpen(false)}
                    width={'full'}
                >
                    <div>
                        <div className={css.headerbox}>
                            {transTitle}
                            <div className={css.headerIcon}>
                                <IconRemove id='closeBtn' onClick={() => setInfoOpen(false)}/>
                            </div>
                        </div>
                        <div className={css.header}>
                            <div className={css.description}>{descDivs}</div>
                        </div>
                        <hr></hr>
                        <div className={css.close}>
                            <SVPButton
                                id='updateSiteBtn'
                                message={strings.Close}
                                noArrow={true}
                                handler={closeInfoHandler}
                                theme={props.theme}/>
                        </div>
                    </div>
                </Dialog>
            );
        } else {
            return (
                null
            );
        }
    }, [props.specDeptData.Transportation, props.theme, infoOpen, transInfo, closeInfoHandler]);

    let timeCards: ReactElement[] | string = '';
    if (!fetchingQuickTimes) {
        if (filteredTimeCards[0] && filteredTimeCards[0].displayDate) {
            timeCards = filteredTimeCards.map(propss => <QuickTimeCard
                id={propss.id}
                key={propss.id}
                qtimeSlots={filteredTimeCards}
                type={quickType}
                checkedId={checkId}
                onChecked={() => onChecked(propss.id)}
                infoClick={infoHandler}
                locale={props.locale}
                specDeptData={props.specDeptData}
                setCheckId={setCheckId}
                setFooterDisabled={setFooterDisabled}
                vehicles={props.vehicles}
                selectedVehicle={props.selectedVehicle}
                theme={props.theme}
                lexusMCLaunched={props.lexusMCLaunched}
                toyotaMCLaunched={props.toyotaMCLaunched}
                lexusTDMCLaunched={props.lexusTDMCLaunched}
                toyotaTDMCLaunched={props.toyotaTDMCLaunched}
                specData={props.specData}
                sessionId={props.sessionId}
                appointment={props.appointmentData}
            />);
            moreOptionsLit = strings.SelectDiffTime;
        } else {
            // if (props.specData?.QuickTimesType === 'A') {
            // if (quickType === 'A') {
            //     timeCards = strings.NoTimeAdvAvailLit;
            // } else {
            //     timeCards = strings.NoTimeTransAvailLit;
            // }
            if (!noFilteredTimes && props.quickTimes?.Built) {
                setNoFilteredTimes(true);
                moreOptionsLit = '';
                diffTimeHandler();
            }
        }
    }

    let advBlockCss = css.advDiv;
    let timeCardCss = css.timeCardDiv;

    // if (props.specData?.QuickTimesType === 'A') {
    if (quickType === 'A') {
        advBlockCss = css.advDiv;
        timeCardCss = css.timeCardDiv;
    } else {
        advBlockCss = css.noAdvDiv;
        timeCardCss = css.timeCardDiv2;
    }

    let avatarColor: 'default' | 'primary' | 'secondary' | 'contrast' | 'completed' | 'blank' = 'primary';
    if (props.theme === 'lexus' || props.theme === 'toyotaSPM' || props.theme === 'audi' || props.theme === 'porsche' ||
        props.theme === 'ford' || props.theme === 'infiniti' || props.theme === 'mercedesBenz'
    ) {
        avatarColor = 'contrast';
    }
    if (props.theme === 'kia' || props.theme === 'hyundai') {
        avatarColor = 'default';
    }
    if (props.theme === 'bmw' || props.theme === 'subaru' || props.theme === 'nissan' || props.theme === 'mazda' ||
            props.theme === 'acura' || props.theme === 'genesis') {
        avatarColor = 'blank';
    }

    //  The Proceed button will go to the Summary screen
    const footerHandler = (e: any) => {
        const selAdvNum = props.qtimeSlots && props.qtimeSlots.length > 0 && !isNaN(parseInt(checkId)) ?
            props.qtimeSlots[checkId].adv : '';
        // const selAdvArr = props.specData?.Advisors?.find((advisors) => advisors.advisorNum === selAdvNum);
        const selAdvArr = props.specDeptData.Advisors?.find((advisors) => advisors.advisorNum === selAdvNum);
        const selAdvName = (selAdvArr) ? selAdvArr?.advisorName : '';
        const selAdvImage = (selAdvArr) ? selAdvArr?.advisorImage : '';
        const selTransFlag = props.qtimeSlots && props.qtimeSlots.length > 0 && !isNaN(parseInt(checkId)) ?
            props.qtimeSlots[checkId].transFlag : '';
        // const selTransArr = props.specData?.Transportation?.find((trans) => trans.type === selTransFlag);
        const selTransArr = props.specDeptData.Transportation?.find((trans) => trans.type === selTransFlag);
        const selTransLits = (selTransArr) ? selTransArr.title : '';
        const inp: UpdateApptDateInp = {
            selDate: '',
            selTime: '',
            selTrans: '',
            selAdv: '',
            selAdvName: '',
            selAdvImage: '',
            selTransFlag: '',
        };
        if (props.qtimeSlots && props.qtimeSlots.length > 0 && !isNaN(parseInt(checkId))) {
            inp.selDate = props.qtimeSlots[checkId].displayDate;
            inp.selTime = props.qtimeSlots[checkId].displayTime;
            inp.selTrans = selTransLits;
            inp.selAdv = props.qtimeSlots[checkId].adv;
            inp.selAdvName = selAdvName;
            inp.selAdvImage = selAdvImage;
            inp.selTransFlag = props.qtimeSlots[checkId].transFlag;
            inp.selApptNum = props.appointment.apptNum;
        }
        props.updateApptDate(inp);
        // When the customer is not found by CustomerSearch, the errMsg property contains a value.
        // If we have ApptCust data then we can skip ahead to the Summary screen
        const vehicle = props.vehicles.find(v => v.id === props.selectedVehicle);
        if ((customerInfo?.errMsg || ((customerInfo?.nameId && customerInfo?.nameId === '') ||
            (vehicle && (vehicle.isOEM === 'Y' && !props.vehicles.some(v => v.isOEM !== 'Y'))))) &&
            (!props.apptCust?.custFirstName || !props.apptCust?.custLastName || !props.apptCust?.custEmailAddress ||
            !props.apptCust?.custPhoneNumber)) {
            props.pushToMainPanes({
                viewId: ComponentMapIds.NewCustInfo,
                component: ComponentMapIds.NewCustInfo,
            });
        } else if ((selTransFlag === 'P' && props.gmossVersion === '1.4.2') || selTransFlag === 'U') {
            props.pushToMainPanes({
                viewId: ComponentMapIds.PickupDelivery,
                component: ComponentMapIds.PickupDelivery,
            });
        } else {
            props.pushToMainPanes({
                viewId: ComponentMapIds.Summary,
                component: ComponentMapIds.Summary,
            });
        }
        const ascRetailProcessInp: ascRetailProcessInput = {
            pageType: 'service',
            flowStage: '5a',
            flowOutcome: 'proceed',
            flowName: 'appointment',
            elementTitle: 'Select Appointment',
        };
        ascRetailProcess(ascRetailProcessInp, props.vehicles, props.selectedVehicle);
        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'service',
            elementText: strings.Proceed,
            elementColor: '',
            elementOrder: '',
            elementType: 'footer',
            elementSubType: 'nav_button',
            eventAction: 'click',
            eventActionResult: 'next_step',
            linkUrl: '',
        };
        ascCtaInteraction(ascCtaInteractionInp, e, props.vehicles, props.selectedVehicle);
        const ascElementConfigurationInp: ascElementConfigurationInput = {
            pageType: 'service',
            elementText: strings.Proceed,
            elementType: 'footer',
            elementColor: '',
            eventAction: 'click',
            eventActionResult: 'next_step',
            linkUrl: '',
        };
        ascElementConfiguration(ascElementConfigurationInp, e);

        if (isSDAnalyticsEnabled) {
            sendSdEvent('scheduleServiceTransportFinish', {
                scheduleServiceSessionId: props.sessionId,
                scheduleServiceProvider: getProviderId(props.specData, props.toyotaMCLaunched,
                    props.toyotaTDMCLaunched, props.lexusMCLaunched, props.lexusTDMCLaunched),
                scheduleServiceTransportType: selTransArr?.title ?? '',
            });
        }
    };

    // //  The DiffTimeHandler will go to the CalendarSelect screen - load invalid dates for the current month
    // const diffTimeHandler = useCallback(() => {
    //     // Need to determine whether any of the operations in ApptOps are recalls
    //     const selectedRecalls =
    //         props.apptOps.filter((apptOp) => apptOp.concern.indexOf(strings.RecallOp) !== -1);

    //     let recallSelected = false;
    //     if (props.checkedRecalls.length > 0 || selectedRecalls.length > 0) {
    //         recallSelected = true;
    //     }
    //     props.clearInvalidDates();
    //     props.getInvalidDates(props.specProfile, undefined, undefined, recallSelected);
    //     setMoveFlag('T');
    // }, [props?.apptOps, props?.checkedRecalls?.length, props?.clearInvalidDates,
    //     props?.getInvalidDates, props?.specProfile]);

    useEffect(() => {
        if (props.qtimeSlots) {
            const currentPane = props.mainPaneViewOrder[props.mainPaneViewOrder.length - 1];
            if (props.qtimeSlots.length && currentPane.viewId === ComponentMapIds.QuickTime && pageViewEvent) {
                const ascPageViewInp: ascPageviewInput = {
                    pageType: 'service',
                    errorCode: '',
                    itemResults: props.qtimeSlots.length.toString(),
                };
                ascPageview(ascPageViewInp, props.vehicles, props.selectedVehicle);
                setPageViewEvent(false);
            }
        }
    }, [props.qtimeSlots, props.mainPaneViewOrder, props.vehicles, props.selectedVehicle, pageViewEvent]);

    useEffect(() => {
        const currentPane = props.mainPaneViewOrder[props.mainPaneViewOrder.length - 1].viewId;
        if (currentPane !== ComponentMapIds.QuickTime) {
            setPageViewEvent(true);
        }
    }, [props.mainPaneViewOrder]);

    const subHeaderClass = classNames(
        css.subHeader,
        {
            [css.subHeaderHonda]: props.theme === 'honda',
            [css.subHeaderBMW]: props.theme === 'bmw',
            [css.subHeaderFord]: props.theme === 'ford',
            [css.subHeaderIN]: props.theme === 'infiniti',
            [css.subHeaderMazda]: props.theme === 'mazda',
            [css.subHeaderAcura]: props.theme === 'acura',
            [css.subHeaderGenesis]: props.theme === 'genesis',
            [css.subHeaderLexus]: props.theme === 'lexus',
        },
    );

    const advStyles = classNames(
        css.adv,
        {
            [css.h4CardHonda]: props.theme === 'honda',
            [css.advBMW]: props.theme === 'bmw',
            [css.advFord]: props.theme === 'ford',
            [css.advIN]: props.theme === 'infiniti',

        },
    );

    const NameStyles = classNames(
        css.Name,
        {
            [css.NameBMW]: props.theme === 'bmw',
            [css.NameMazda]: props.theme === 'mazda',
            [css.NameGenesis]: props.theme === 'genesis',
            [css.NameLexus]: props.theme === 'lexus',
        },
    );

    return (
        <div>
            <div className={css.mainDiv}>
                {fetchingQuickTimes ? <LoadingProgress cssClasses=
                    {{ spinner: css.progressColor }} size={100} /> : null}
                {moveFlag ? <LoadingProgress cssClasses={{ spinner: css.progressColor }} size={100} /> : null}
                <h2 className={NameStyles}>{firstName + ','}</h2>
                <div style={{ display: 'flex' }}>
                    <h3>{header}</h3>
                    {!fetchingQuickTimes ?
                        <h3 className={css.subHeaderA}>{subHeaderA}</h3> : null
                    }
                    {!fetchingQuickTimes ?
                        <div>{infoIcon}</div> : null
                    }
                </div>
                <div className={advBlockCss}>
                    <hr />
                    {!fetchingQuickTimes ?
                        <div style={{ display: 'flex' }}>
                            {(props.theme === 'hyundai' && !displayAdvImage)? (
                                <IconProfile2
                                    className={css.avatarHyundai}
                                />
                            ) : (
                                <Avatar
                                    src={displayAdvImage ? displayAdvImage : noProfileImage}
                                    elevation={0}
                                    size={60}
                                    color={avatarColor}
                                    iconColor='contrast'
                                    alt={displayAdvImgAltTag}
                                    borderThickness={0}
                                />
                            )}
                            <div className={advStyles}>
                                <span>{displayAdvName}</span>
                            </div>
                        </div> : null
                    }
                </div>
                <hr />
                <div className={css.gridRow}>
                    <div className={css.gridLeft}>
                        <h4 className={subHeaderClass}>{strings.DateTime}</h4>
                    </div>
                    <div className={css.gridRight}>
                        <h4 className={subHeaderClass}>{subHeaderB}</h4>
                    </div>
                </div>
                <hr />
                <div className={timeCardCss}>
                    {fetchingQuickTimes ? (
                        <div className={css.spinnerOverlay}>
                            <LoadingProgress cssClasses={{ spinner: css.progressColor }} size={100} />
                        </div>
                    ) : (
                        <ScrollBar scrollVertical>
                            <div>
                                {transDescription}
                                {timeCards}
                            </div>
                            <div className={css.moreOptionsLit}>
                                {moreOptionsLit}
                            </div>
                        </ScrollBar>
                    )}
                </div>
            </div >
            {/* {specialConsiderationMessage} */}
            <Footer
                id='QuickTimeFooter'
                handler={footerHandler}
                isDisabled={footerDisabled}
                secondaryMessage={strings.DifferentTime}
                secondaryhandler={diffTimeHandler}
                theme={props.theme}/>
        </div>
    );
};

export default QuickTime;